// AGC: Firestore query helpers for the admin analytics dashboard.
// AGC: All queries load events in the date range once, then aggregate in memory.
// AGC: A 30s in-memory cache prevents repeated Firestore reads during one dashboard render.
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'

// ---------------------------------------------------------------------------
// AGC: Public types
// ---------------------------------------------------------------------------

export type DateRange = '24h' | '7d' | '30d' | '90d'

export interface OverviewStats {
  totalEvents: number
  activeUsers: number
  topFeature: { name: string; count: number } | null
  errorRatePct: number
}

export interface FeatureUsageRow {
  feature_name: string
  count: number
}

export interface EntityCreationRow {
  entity_type: string
  count: number
}

export interface CreationSourceRow {
  source: string
  count: number
}

export interface VoiceFrictionRow {
  entity_type: string
  firstTrySuccess: number
  retries: number
  failures: number
  retryRatePct: number
}

export interface VoiceErrorRow {
  error_code: string
  count: number
}

export interface WizardAbandonRow {
  wizard_name: string
  last_step_name: string
  count: number
}

export interface FormFailureRow {
  form_name: string
  error_code: string
  count: number
}

export interface ActiveUserPoint {
  date: string
  activeUsers: number
}

// ---------------------------------------------------------------------------
// AGC: Internal types
// ---------------------------------------------------------------------------

interface RawEvent {
  name: string
  params: Record<string, unknown>
  userId: string | null
  timestamp: Date
}

interface CacheEntry {
  events: RawEvent[]
  expiresAt: number
}

// ---------------------------------------------------------------------------
// AGC: Cache
// ---------------------------------------------------------------------------

const cache = new Map<DateRange, CacheEntry>()
const CACHE_TTL_MS = 30_000

/** AGC: Test-only helper — clear the in-memory cache between test runs. */
export function _clearCacheForTests(): void {
  cache.clear()
}

// ---------------------------------------------------------------------------
// AGC: Fetch helpers
// ---------------------------------------------------------------------------

function rangeToMs(range: DateRange): number {
  switch (range) {
    case '24h': return 24 * 60 * 60 * 1000
    case '7d':  return 7 * 24 * 60 * 60 * 1000
    case '30d': return 30 * 24 * 60 * 60 * 1000
    case '90d': return 90 * 24 * 60 * 60 * 1000
  }
}

async function fetchEvents(range: DateRange): Promise<RawEvent[]> {
  const since = Timestamp.fromMillis(Date.now() - rangeToMs(range))
  const q = query(
    collection(db, 'analytics_events'),
    where('timestamp', '>=', since),
    orderBy('timestamp', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => {
    const data = d.data()
    return {
      name: data['name'] as string,
      params: (data['params'] ?? {}) as Record<string, unknown>,
      userId: (data['userId'] ?? null) as string | null,
      timestamp: (data['timestamp']?.toDate?.() as Date) ?? new Date(),
    }
  })
}

async function getEvents(range: DateRange): Promise<RawEvent[]> {
  const cached = cache.get(range)
  if (cached && cached.expiresAt > Date.now()) return cached.events
  const events = await fetchEvents(range)
  cache.set(range, { events, expiresAt: Date.now() + CACHE_TTL_MS })
  return events
}

// ---------------------------------------------------------------------------
// AGC: Public query functions
// ---------------------------------------------------------------------------

/** AGC: High-level summary: total events, unique users, top feature, error rate. */
export async function getOverviewStats(range: DateRange): Promise<OverviewStats> {
  const events = await getEvents(range)
  const totalEvents = events.length

  if (totalEvents === 0) {
    return { totalEvents: 0, activeUsers: 0, topFeature: null, errorRatePct: 0 }
  }

  // AGC: Count unique non-null userIds
  const userIds = new Set<string>()
  for (const e of events) {
    if (e.userId) userIds.add(e.userId)
  }

  // AGC: Count feature_used by feature_name, track max
  const featureCounts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'feature_used') continue
    const feature = e.params['feature_name'] as string | undefined
    if (!feature) continue
    featureCounts.set(feature, (featureCounts.get(feature) ?? 0) + 1)
  }

  let topFeature: { name: string; count: number } | null = null
  for (const [name, count] of featureCounts) {
    if (!topFeature || count > topFeature.count) {
      topFeature = { name, count }
    }
  }

  // AGC: Error rate = voice_session_fail + form_submit_fail events / total
  let errorCount = 0
  for (const e of events) {
    if (e.name === 'voice_session_fail' || e.name === 'form_submit_fail') errorCount++
  }
  const errorRatePct = Math.round((errorCount / totalEvents) * 1000) / 10

  return {
    totalEvents,
    activeUsers: userIds.size,
    topFeature,
    errorRatePct,
  }
}

/** AGC: Top N features by usage count, sorted descending. */
export async function getTopFeatures(range: DateRange, limit = 10): Promise<FeatureUsageRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'feature_used') continue
    const feature = e.params['feature_name'] as string | undefined
    if (!feature) continue
    counts.set(feature, (counts.get(feature) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([feature_name, count]) => ({ feature_name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

/** AGC: entity_created events grouped by entity_type, sorted desc. */
export async function getEntityCreations(range: DateRange): Promise<EntityCreationRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'entity_created') continue
    const entityType = e.params['entity_type'] as string | undefined
    if (!entityType) continue
    counts.set(entityType, (counts.get(entityType) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([entity_type, count]) => ({ entity_type, count }))
    .sort((a, b) => b.count - a.count)
}

/** AGC: entity_created events grouped by source (voice/form/wizard/etc), sorted desc. */
export async function getCreationSources(range: DateRange): Promise<CreationSourceRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'entity_created') continue
    const source = e.params['source'] as string | undefined
    if (!source) continue
    counts.set(source, (counts.get(source) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count)
}

/** AGC: Voice retry and failure stats per entity_type. Sorted worst retry rate first. */
export async function getVoiceFrictionStats(range: DateRange): Promise<VoiceFrictionRow[]> {
  const events = await getEvents(range)

  interface Accum {
    firstTrySuccess: number
    retries: number
    failures: number
  }

  const accum = new Map<string, Accum>()

  const ensure = (entityType: string): Accum => {
    if (!accum.has(entityType)) {
      accum.set(entityType, { firstTrySuccess: 0, retries: 0, failures: 0 })
    }
    return accum.get(entityType)!
  }

  for (const e of events) {
    if (e.name === 'voice_session_success') {
      const entityType = e.params['entity_type'] as string | undefined
      if (!entityType) continue
      const attemptNumber = e.params['attempt_number'] as number | undefined
      const row = ensure(entityType)
      if (attemptNumber === 1) {
        row.firstTrySuccess++
      } else {
        row.retries++
      }
    } else if (e.name === 'voice_session_fail') {
      const entityType = e.params['entity_type'] as string | undefined
      if (!entityType) continue
      const row = ensure(entityType)
      row.failures++
    }
  }

  return Array.from(accum.entries())
    .map(([entity_type, row]) => {
      const denominator = row.firstTrySuccess + row.retries
      const retryRatePct = denominator === 0
        ? 0
        : Math.round((row.retries / denominator) * 1000) / 10
      return {
        entity_type,
        firstTrySuccess: row.firstTrySuccess,
        retries: row.retries,
        failures: row.failures,
        retryRatePct,
      }
    })
    // AGC: Worst retry rate first; total activity as tiebreaker
    .sort((a, b) => {
      if (b.retryRatePct !== a.retryRatePct) return b.retryRatePct - a.retryRatePct
      const totalA = a.firstTrySuccess + a.retries + a.failures
      const totalB = b.firstTrySuccess + b.retries + b.failures
      return totalB - totalA
    })
}

/** AGC: voice_session_fail events grouped by error_code, sorted desc. */
export async function getVoiceErrors(range: DateRange): Promise<VoiceErrorRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'voice_session_fail') continue
    const errorCode = e.params['error_code'] as string | undefined
    if (!errorCode) continue
    counts.set(errorCode, (counts.get(errorCode) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([error_code, count]) => ({ error_code, count }))
    .sort((a, b) => b.count - a.count)
}

/** AGC: wizard_abandon events grouped by {wizard_name, last_step_name} tuple, sorted desc. */
export async function getWizardAbandons(range: DateRange): Promise<WizardAbandonRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'wizard_abandon') continue
    const wizardName = e.params['wizard_name'] as string | undefined
    const lastStepName = e.params['last_step_name'] as string | undefined
    if (!wizardName || !lastStepName) continue
    const key = `${wizardName}|||${lastStepName}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([key, count]) => {
      const [wizard_name, last_step_name] = key.split('|||') as [string, string]
      return { wizard_name, last_step_name, count }
    })
    .sort((a, b) => b.count - a.count)
}

/** AGC: form_submit_fail events grouped by {form_name, error_code} tuple, sorted desc. */
export async function getFormFailures(range: DateRange): Promise<FormFailureRow[]> {
  const events = await getEvents(range)
  const counts = new Map<string, number>()
  for (const e of events) {
    if (e.name !== 'form_submit_fail') continue
    const formName = e.params['form_name'] as string | undefined
    const errorCode = e.params['error_code'] as string | undefined
    if (!formName || !errorCode) continue
    const key = `${formName}|||${errorCode}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([key, count]) => {
      const [form_name, error_code] = key.split('|||') as [string, string]
      return { form_name, error_code, count }
    })
    .sort((a, b) => b.count - a.count)
}

/** AGC: Unique active users per calendar day within the range, oldest-first, zero-padded. */
export async function getActiveUsersPerDay(range: DateRange): Promise<ActiveUserPoint[]> {
  const events = await getEvents(range)

  // AGC: Build a map of YYYY-MM-DD → Set<userId>
  const dayMap = new Map<string, Set<string>>()
  for (const e of events) {
    if (!e.userId) continue
    const dateKey = e.timestamp.toISOString().slice(0, 10)
    if (!dayMap.has(dateKey)) dayMap.set(dateKey, new Set())
    dayMap.get(dateKey)!.add(e.userId)
  }

  // AGC: Determine how many calendar days to emit:
  // AGC: 24h → 1 day (today only), 7d → 7 days, 30d → 30 days, 90d → 90 days.
  const dayCount =
    range === '24h' ? 1
    : range === '7d'  ? 7
    : range === '30d' ? 30
    : 90

  // AGC: Build result array: [today - (dayCount-1), ..., today], oldest first
  const now = new Date()
  const todayStr = now.toISOString().slice(0, 10)
  const result: ActiveUserPoint[] = []

  for (let i = dayCount - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setUTCDate(d.getUTCDate() - i)
    const dateKey = d.toISOString().slice(0, 10)
    const usersSet = dayMap.get(dateKey)
    result.push({ date: dateKey, activeUsers: usersSet ? usersSet.size : 0 })
  }

  // AGC: ensure the last point is today
  void todayStr
  return result
}
